import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Avatar, Badge, Dropdown, Input, Layout, Grid } from 'antd';
import {
  AppstoreFilled,
  AppstoreOutlined,
  HomeFilled,
  ReadFilled,
  ShoppingFilled,
  TruckOutlined,
  DownOutlined,
  EnvironmentFilled,
  HeartOutlined,
  HomeOutlined,
  LogoutOutlined,
  ReadOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  UserOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@core/hooks/useAuth';
import { useAccountSelectionStore } from '@core/store/accountSelectionStore';
import { useActiveCart } from '@core/hooks/useCart';
import { useCategories } from '@core/hooks/useCatalog';
import { pickBrowseCategories } from '@core/utils/categories';
import { useLocations } from '@core/hooks/useLocations';
import { useRuntimeBrand } from '@core/hooks/useRuntimeBrand';
import { useStoreContext } from '@core/hooks/useStoreContext';
import { useUiStore } from '@core/store/uiStore';
import { useWishlistStore } from '@core/store/wishlistStore';
import { getAvatarStyles } from '@core/utils/avatar';
import CartDrawer from '@components/base/CartDrawer';
import CheckoutModal from '@components/base/CheckoutModal';
import {
  CHECKOUT_MODAL_REDIRECT,
  loadCheckoutModule,
  openCheckoutModal,
} from '@components/base/CheckoutModal/checkoutModalEvents.js';
import ImageWithFallback from '@components/base/ImageWithFallback';
import MobileNav from '@components/base/MobileNav';
import StorefrontAuthModal from '@components/base/StorefrontAuthModal';
import { useAppLanguage } from '@/app/providers/useAppLanguage.js';
import { useCopy } from '@/app/providers/useCopy';
import { useIndustry } from '@/app/providers/industry-context.js';

const { Header } = Layout;

const CURRENT_LOCATION_ID = 'temp-current-location';
const NAV_CATEGORY_LIMIT = 8;

// Fish skin fonts (Hanken Grotesk body, Poppins promo titles); injected once,
// fish only.
const COASTAL_FONTS_ID = 'fish-coastal-fonts';
const COASTAL_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@300;400;500;600;700;800&family=Playfair+Display:wght@500;600;700&family=Poppins:wght@500;600;700;800&display=swap';

// True once the page has scrolled past `threshold` px. The storefront scrolls
// an inner container rather than the window, so listen in the capture phase
// and read whichever element fired.
// Utility-row icons, keyed by link path (config.header.utilityLinks).
const UTILITY_ICONS = {
  '/': <HomeOutlined />,
  '/account': <UserOutlined />,
  '/orders': <TruckOutlined />,
  '/wishlist': <HeartOutlined />,
};

function useHasScrolled(threshold = 24) {
  const [hasScrolled, setHasScrolled] = useState(false);
  useEffect(() => {
    const onScroll = (event) => {
      const target = event.target;
      const top =
        target === document || target === document.documentElement
          ? window.scrollY
          : (target?.scrollTop ?? 0);
      setHasScrolled(top > threshold);
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', onScroll, { capture: true });
  }, [threshold]);
  return hasScrolled;
}

function useCoastalFonts() {
  useEffect(() => {
    if (document.getElementById(COASTAL_FONTS_ID)) return;
    const link = document.createElement('link');
    link.id = COASTAL_FONTS_ID;
    link.rel = 'stylesheet';
    link.href = COASTAL_FONTS_HREF;
    document.head.appendChild(link);
  }, []);
}

const fallbackTamilNaduLocations = [
  { id: 'temp-tn-chennai', name: 'Chennai', city: 'Chennai', state: 'Tamil Nadu' },
  { id: 'temp-tn-coimbatore', name: 'Coimbatore', city: 'Coimbatore', state: 'Tamil Nadu' },
  { id: 'temp-tn-madurai', name: 'Madurai', city: 'Madurai', state: 'Tamil Nadu' },
  { id: 'temp-tn-trichy', name: 'Tiruchirappalli', city: 'Tiruchirappalli', state: 'Tamil Nadu' },
  { id: 'temp-tn-salem', name: 'Salem', city: 'Salem', state: 'Tamil Nadu' },
];

export default function HeaderFish() {
  const t = useCopy();
  const { config } = useIndustry();
  useAppLanguage();
  useCoastalFonts();
  const hasScrolled = useHasScrolled();
  const { logoUrl: runtimeLogoUrl, brandName: runtimeBrandName } = useRuntimeBrand();
  const { customer, isAuthenticated, logout } = useAuth();
  const openAccountSelector = useAccountSelectionStore((s) => s.openAccountSelector);
  const hasLinkedAccounts = useAccountSelectionStore((s) => s.hasLinkedAccounts);
  const { lineCount } = useActiveCart();
  const categoriesQuery = useCategories({ limit: 50 });
  const navCategories = useMemo(
    () => pickBrowseCategories(categoriesQuery.data ?? []).slice(0, NAV_CATEGORY_LIMIT),
    [categoriesQuery.data],
  );
  const { data: locations = [] } = useLocations();
  const { locationId } = useStoreContext();
  const openStoreSelector = useUiStore((s) => s.openStoreSelector);
  const wishlistCount = useWishlistStore((s) => s.items.length);
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, search } = location;
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const [navOpen, setNavOpen] = useState(false);
  const [authMode, setAuthMode] = useState(null);
  const [authRedirect, setAuthRedirect] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const isSearchAllowedPage =
    pathname === '/' ||
    pathname === '/catalog' ||
    pathname === '/categories' ||
    pathname.startsWith('/catalog') ||
    pathname.startsWith('/categories');
  const shouldHideHeaderSearch = !isSearchAllowedPage;

  useEffect(() => {
    if (shouldHideHeaderSearch) setMobileSearchOpen(false);
  }, [shouldHideHeaderSearch]);

  // On a page change the app search box closes — unless the new page carries
  // a search (home → /catalog?q=…), so the typed text stays visible.
  const searchRef = useRef(search);
  searchRef.current = search;
  useEffect(() => {
    setMobileSearchOpen(Boolean(new URLSearchParams(searchRef.current).get('q')));
  }, [pathname]);

  // Opening search from the header icon puts the cursor straight in the field.
  useEffect(() => {
    if (mobileSearchOpen) searchInputRef.current?.focus();
  }, [mobileSearchOpen]);

  const navItems = config?.nav?.primary ?? [];
  const appName = runtimeBrandName || config?.copy?.appName || config?.name || 'Home';
  const displayName = customer?.first_name || customer?.email?.split('@')[0] || 'Account';
  const selectableLocations = locations.length ? locations : fallbackTamilNaduLocations;
  const currentLocationName = t('currentLocation', 'Current location');
  const selectedLocation =
    locationId === CURRENT_LOCATION_ID
      ? { id: CURRENT_LOCATION_ID, name: currentLocationName, city: currentLocationName }
      : selectableLocations.find((loc) => loc.id === locationId);
  const activeLocation = selectedLocation ?? selectableLocations[0];
  const selectedLocationLabel =
    activeLocation?.name ||
    [activeLocation?.city, activeLocation?.state].filter(Boolean).join(', ') ||
    '';
  const hasSelectedMobileLocation = Boolean(selectedLocation);
  const mobileLocationLabel =
    selectedLocation?.name ||
    [selectedLocation?.city, selectedLocation?.state].filter(Boolean).join(', ') ||
    '';
  const accountItems = [
    { key: 'email', label: customer?.email ?? displayName, disabled: true },
    { type: 'divider' },
    { key: 'profile', icon: <UserOutlined />, label: t('profile', 'Profile') },
    { key: 'wishlist', icon: <HeartOutlined />, label: t('wishlist', 'Wishlist') },
    // {
    //   key: 'switch_account',
    //   icon: <UserSwitchOutlined />,
    //   label: t('switchAccount', 'Switch Account'),
    // },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: t('signOut', 'Sign out') },
  ];

  const onAccountClick = ({ key }) => {
    if (key === 'profile') navigate('/account');
    if (key === 'switch_account') openAccountSelector();
    if (key === 'orders') navigate('/orders');
    if (key === 'wishlist') navigate('/wishlist', { state: { refreshFavoritesAt: Date.now() } });
    if (key === 'logout') {
      logout();
      navigate('/');
    }
  };

  const onSearch = (value) => {
    if (shouldHideHeaderSearch) return;
    const q = value.trim();
    navigate(q ? `/catalog?q=${encodeURIComponent(q)}` : '/catalog');
  };

  // Keep the box in step with the URL (same as restaurant): it shows the
  // active `?q=`, and empties when the search is cleared or you navigate away.
  useEffect(() => {
    const query = new URLSearchParams(search).get('q') || '';
    setSearchText(query);
  }, [search]);

  const onOrdersClick = () => {
    if (isAuthenticated) {
      navigate('/orders');
      return;
    }
    setAuthRedirect('/orders');
    setAuthMode('login');
  };

  const brandLink = (
    <Link to="/" className="fish-brand" aria-label={`${appName} home`}>
      {runtimeLogoUrl || config?.logoUrl ? (
        <ImageWithFallback
          src={runtimeLogoUrl || config.logoUrl}
          alt=""
          className="fish-brand-mark"
          style={{
            width: '2.5rem',
            height: '2.5rem',
            maxWidth: '32rem',
            maxHeight: '32rem',
            objectFit: 'contain',
          }}
        />
      ) : (
        <span className="fish-brand-mark" aria-hidden="true">
          ~
        </span>
      )}
    </Link>
  );

  // Desktop utility row (config.header). Wishlist sits on the right end.
  const headerCopy = config?.header ?? {};
  const allUtilityLinks = headerCopy.utilityLinks ?? [];
  const utilityLinks = allUtilityLinks.filter((link) => link.to !== '/wishlist');
  const wishlistLink = allUtilityLinks.find((link) => link.to === '/wishlist');
  // Account / orders need a session; route signed-out users through login.
  const PROTECTED_PATHS = ['/account', '/orders'];
  const onUtilityClick = (event, to) => {
    if (isAuthenticated || !PROTECTED_PATHS.some((path) => to.startsWith(path))) return;
    event.preventDefault();
    setAuthRedirect(to);
    setAuthMode('login');
  };

  const mobileTabs = [
    {
      key: 'home',
      to: '/',
      label: t('home', 'Home'),
      icon: <HomeOutlined />,
      activeIcon: <HomeFilled />,
    },
    {
      key: 'category',
      to: '/catalog?view=categories',
      label: t('category', 'Category'),
      icon: <AppstoreOutlined />,
      activeIcon: <AppstoreFilled />,
    },
    {
      key: 'shop',
      to: '/catalog',
      label: t('shop', 'Shop'),
      icon: <ShoppingOutlined />,
      activeIcon: <ShoppingFilled />,
    },
    {
      key: 'orders',
      to: '/orders',
      label: t('orders', 'Orders'),
      icon: <ReadOutlined />,
      activeIcon: <ReadFilled />,
    },
  ];

  const isTabActive = (tab) => {
    const searchParams = new URLSearchParams(search);
    const isCategoryView = searchParams.get('view') === 'categories';
    if (tab.key === 'home') return pathname === '/';
    if (tab.key === 'category') return pathname.startsWith('/catalog') && isCategoryView;
    if (tab.key === 'shop') return pathname === '/catalog' && !isCategoryView;
    return pathname.startsWith(tab.to);
  };

  useEffect(() => {
    if (shouldHideHeaderSearch) setMobileSearchOpen(false);
  }, [shouldHideHeaderSearch]);

  useEffect(() => {
    if (!location.state?.openCartDrawer) return;
    setCartOpen(true);
    navigate(`${pathname}${search}`, { replace: true, state: null });
  }, [location.state, navigate, pathname, search]);

  useEffect(() => {
    const handleOpenAuthModal = (e) => {
      const { mode = 'login', redirect = null } = e.detail || {};
      setAuthMode(mode);
      if (redirect) setAuthRedirect(redirect);
    };
    window.addEventListener('open-auth-modal', handleOpenAuthModal);
    return () => window.removeEventListener('open-auth-modal', handleOpenAuthModal);
  }, []);

  const closeCart = useCallback(() => setCartOpen(false), []);
  // Checkout opens in a modal over the page (coffee flow); after a login it
  // reopens there instead of routing to /checkout.
  const goToCheckout = () => {
    if (isAuthenticated) {
      openCheckoutModal();
      return;
    }
    setAuthRedirect(CHECKOUT_MODAL_REDIRECT);
    setAuthMode('login');
  };

  // Fetch the checkout code while the cart is open so the modal opens fast.
  useEffect(() => {
    if (cartOpen) loadCheckoutModule();
  }, [cartOpen]);

  return (
    <>
      <Header
        className="fish-header"
        ref={(el) => {
          if (!el) return;
          const observer = new ResizeObserver((entries) => {
            document.documentElement.style.setProperty(
              '--fish-header-height',
              `${entries[0].target.offsetHeight}px`,
            );
          });
          observer.observe(el);
        }}
      >
        <div className={`fx-nav__band ${isMobile ? '' : 'is-desktop'}`}>
          {!isMobile ? brandLink : null}
          {!isMobile ? (
            <div className="fx-nav__utility">
              <nav className="fx-nav__links" aria-label={t('quickLinks', 'Quick links')}>
                {utilityLinks.map((link) => {
                  const isActive =
                    link.to === '/' ? pathname === '/' : pathname.startsWith(link.to);
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={isActive ? 'is-active' : undefined}
                      aria-current={isActive ? 'page' : undefined}
                      onClick={(event) => onUtilityClick(event, link.to)}
                    >
                      {UTILITY_ICONS[link.to] ?? null}
                      {link.label}
                    </Link>
                  );
                })}
              </nav>
              {wishlistLink ? (
                <Link
                  to="/wishlist"
                  state={{ refreshFavoritesAt: Date.now() }}
                  className={`fx-nav__end ${pathname.startsWith('/wishlist') ? 'is-active' : ''}`}
                  aria-current={pathname.startsWith('/wishlist') ? 'page' : undefined}
                >
                  <HeartOutlined aria-hidden />
                  {wishlistLink.label}
                  {wishlistCount > 0 ? (
                    <span className="fx-nav__end-count">{wishlistCount}</span>
                  ) : null}
                </Link>
              ) : null}
            </div>
          ) : null}
          <div
            className={`fish-header-inner ${isMobile ? 'is-mobile' : ''} ${
              shouldHideHeaderSearch ? 'hide-search' : ''
            } ${hasLinkedAccounts ? 'has-account-switcher' : ''}`}
          >
            {isMobile ? brandLink : null}

            {!isMobile ? (
              <button
                type="button"
                className="fish-delivery-pill"
                aria-label={t('changeStore', 'Change store')}
                onClick={openStoreSelector}
              >
                <img src="/location-pin.png" alt="Location" className="location-icon-img" />
                {selectedLocation ? (
                  <span>
                    <strong>{selectedLocationLabel}</strong>
                  </span>
                ) : null}
                <DownOutlined className="fx-nav__pill-caret" aria-hidden />
              </button>
            ) : null}

            {isMobile ? (
              <button
                type="button"
                className={
                  hasSelectedMobileLocation
                    ? 'fish-delivery-pill fish-delivery-pill-mobile is-selected'
                    : 'fish-delivery-pill fish-delivery-pill-mobile'
                }
                aria-label={t('changeStore', 'Change store')}
                onClick={openStoreSelector}
              >
                <EnvironmentFilled className="fish-delivery-pill-pin" aria-hidden />
                <span className="fish-delivery-pill-text">
                  <strong>{mobileLocationLabel || ''}</strong>
                </span>
                <DownOutlined className="fx-nav__pill-caret" aria-hidden />
              </button>
            ) : null}

            <div
              className={`fish-search ${mobileSearchOpen ? 'open' : ''} ${
                shouldHideHeaderSearch ? 'is-hidden' : ''
              }`}
            >
              <Input
                ref={searchInputRef}
                placeholder={t('searchPlaceholder', 'Search your products')}
                value={searchText}
                disabled={shouldHideHeaderSearch}
                onChange={(event) => {
                  if (shouldHideHeaderSearch) return;
                  const value = event.target.value;
                  setSearchText(value);
                  if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
                  searchTimeoutRef.current = setTimeout(() => {
                    onSearch(value);
                  }, 300);
                }}
                onPressEnter={() => {
                  if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
                  onSearch(searchText);
                }}
                prefix={<SearchOutlined style={{ color: 'var(--color-text-secondary)' }} />}
                aria-label="Search products"
                allowClear
              />
              {!isMobile ? (
                <button
                  type="button"
                  className="fx-search-submit"
                  aria-label={t('search', 'Search')}
                  onClick={() => {
                    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
                    onSearch(searchText);
                  }}
                >
                  <SearchOutlined />
                </button>
              ) : null}
            </div>

            <div className="fish-header-actions">
              {!shouldHideHeaderSearch ? (
                <button
                  type="button"
                  className={`fish-header-icon-btn fish-search-btn ${mobileSearchOpen ? 'active' : ''}`}
                  onClick={() => setMobileSearchOpen((prev) => !prev)}
                  aria-label="Search"
                  aria-expanded={mobileSearchOpen}
                >
                  <SearchOutlined />
                </button>
              ) : null}

              {!isMobile ? (
                <button
                  className="fx-nav__pill fx-nav__pill--cart"
                  type="button"
                  aria-label="Open cart"
                  onClick={() => setCartOpen(true)}
                >
                  <ShoppingCartOutlined aria-hidden />
                  {t('cart', 'Cart')}
                  <span className="fx-nav__pill-count" aria-label={`${lineCount} items`}>
                    {lineCount}
                  </span>
                </button>
              ) : null}

              {isAuthenticated && !isMobile ? (
                <Dropdown
                  menu={{ items: accountItems, onClick: onAccountClick }}
                  trigger={['click']}
                >
                  <button
                    type="button"
                    className="fx-nav__pill fx-nav__pill--account"
                    aria-label="Account"
                  >
                    <UserOutlined aria-hidden />
                    {displayName}
                  </button>
                </Dropdown>
              ) : isAuthenticated ? (
                <Dropdown
                  menu={{ items: accountItems, onClick: onAccountClick }}
                  trigger={['click']}
                >
                  <button
                    type="button"
                    className="fish-header-icon-btn fish-account-menu-button"
                    aria-label="Account"
                  >
                    <Avatar
                      className="fish-header-account-avatar"
                      style={getAvatarStyles(displayName)}
                    >
                      {displayName[0]?.toUpperCase()}
                    </Avatar>
                  </button>
                </Dropdown>
              ) : !isMobile ? (
                <button
                  className="fx-nav__pill fx-nav__pill--account"
                  type="button"
                  onClick={() => setAuthMode('choice')}
                >
                  <UserOutlined aria-hidden />
                  {t('login', 'Login')}
                </button>
              ) : (
                <button
                  className="fish-header-icon-btn"
                  type="button"
                  aria-label="Account"
                  onClick={() => setAuthMode('choice')}
                >
                  <UserOutlined />
                </button>
              )}
            </div>
          </div>
        </div>
        {/* The catalog page has its own category chips; skip the band there and
            on My Orders, where it covered the status tabs. */}
        {!isMobile &&
        navCategories.length &&
        !pathname.startsWith('/catalog') &&
        !pathname.startsWith('/orders') ? (
          <nav
            className={`fx-catnav ${hasScrolled ? 'is-visible' : ''}`}
            aria-label={t('category', 'Categories')}
            aria-hidden={!hasScrolled}
            inert={hasScrolled ? undefined : ''}
          >
            <div className="fx-catnav__inner">
              <Link
                to="/catalog"
                className={`fx-catnav__link ${pathname === '/catalog' && !search.includes('category=') ? 'is-active' : ''}`}
              >
                {t('allProducts', 'All products')}
              </Link>
              {navCategories.map((category) => (
                <Link
                  key={category.id}
                  to={`/catalog?category=${category.id}`}
                  className={`fx-catnav__link ${search.includes(`category=${category.id}`) ? 'is-active' : ''}`}
                >
                  {category.name}
                </Link>
              ))}
            </div>
          </nav>
        ) : null}
      </Header>

      {isMobile ? (
        <nav className="fish-mobile-tabs" aria-label="Primary navigation">
          {mobileTabs.map((tab) => {
            const isActive = isTabActive(tab);
            const icon = isActive ? (tab.activeIcon ?? tab.icon) : tab.icon;
            return (
              <Link
                key={tab.key}
                to={tab.to}
                state={tab.state}
                onClick={(e) => {
                  if (tab.onClick) {
                    e.preventDefault();
                    tab.onClick();
                  } else if (tab.to === '/orders' && !isAuthenticated) {
                    e.preventDefault();
                    onOrdersClick();
                  }
                }}
                className={`fish-mobile-tab ${isActive ? 'active' : ''}`}
                aria-current={isActive ? 'page' : undefined}
              >
                <span className="fish-mobile-tab-icon">
                  {tab.count ? (
                    <Badge count={tab.count} size="small" offset={[9, -4]}>
                      {icon}
                    </Badge>
                  ) : (
                    icon
                  )}
                </span>
                <span className="fish-mobile-tab-label">{tab.label}</span>
              </Link>
            );
          })}
        </nav>
      ) : null}
      <MobileNav open={navOpen} onClose={() => setNavOpen(false)} items={navItems} />
      <CartDrawer
        open={cartOpen}
        onClose={closeCart}
        onCheckout={goToCheckout}
        classPrefix="fish"
        editModalClassName="grocery-product-modal"
      />
      <StorefrontAuthModal
        open={Boolean(authMode)}
        mode={authMode ?? 'choice'}
        onCancel={() => {
          setAuthMode(null);
          setAuthRedirect(null);
        }}
        onAuthenticated={() => {
          if (authRedirect === CHECKOUT_MODAL_REDIRECT) openCheckoutModal();
          else if (authRedirect) navigate(authRedirect);
          setAuthRedirect(null);
        }}
      />
      <CheckoutModal className="fish-checkout-modal" />
    </>
  );
}
